import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { getEducationalPosts } from "../../api";
import WelcomeModal from "../../components/common/WelcomeModal";
import useGoToPricing from "../../hooks/useGoToPricing";
import { getPublishers } from "../../flux/publisherAction";
import publisherStore from "../../flux/publisherStore";
import { isLoggedIn } from "../../utils/isLoggedIn";
import { getPublisherAvatarUrl } from "../../utils";
import { scrollPageToTop } from "../../utils/scrollPageToTop";
import Header from "../Homepage/sections/Header";
import Footer from "../Homepage/sections/Footer";
import { HomepageGlobalStyles } from "../Homepage/styles";
import MemberFeedSkeleton from "./MemberFeedSkeleton";
import MembersDateFilter from "./MembersDateFilter";
import MembersPagination from "./MembersPagination";
import { LeadMemberPostCard, MemberPostCard } from "./MemberPostCard";
import {
  POSTS_PER_PAGE,
  buildMemberFilter,
} from "./memberUtils";
import {
  MembersAllLabel,
  MembersAnalystButton,
  MembersAnalystFilters,
  MembersClearButton,
  MembersContentInner,
  MembersCta,
  MembersCtaBg,
  MembersCtaButton,
  MembersCtaInner,
  MembersCtaLead,
  MembersCtaTitle,
  MembersEmptyState,
  MembersFeedSection,
  MembersFilterActions,
  MembersFilterCard,
  MembersFiltersSection,
  MembersGrid,
  MembersGuestBanner,
  MembersGuestBannerActions,
  MembersGuestBannerText,
  MembersHero,
  MembersHeroBgGradient,
  MembersHeroBgRadial,
  MembersHeroCtaButton,
  MembersHeroInner,
  MembersHeroTitle,
  MembersOutlineButton,
  MembersPage,
  MembersPaginationWrap,
} from "./Members.styles";

const Members = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const goToPricing = useGoToPricing();
  const loggedIn = isLoggedIn();

  const [publishersSelected, setPublishersSelected] = useState([]);
  const [publishers, setPublishers] = useState(
    publisherStore.getPublishers() || []
  );
  const [posts, setPosts] = useState([]);
  const [total, setTotal] = useState(0);
  const [activePage, setActivePage] = useState(1);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [isClearDates, setIsClearDates] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);

  const userDetails = location.state?.details;
  const renewal = location.state?.renewal;

  const visiblePublishers = useMemo(
    () => (publishers || []).filter((publisher) => publisher.isHidden === false),
    [publishers]
  );

  const hasActiveFilters =
    publishersSelected.length > 0 || !!startDate || !!endDate;

  const totalPages = Math.max(1, Math.ceil(total / POSTS_PER_PAGE));

  const fetchPosts = useCallback(
    async (pageIndex, authorIds, fromDate, toDate) => {
      setLoading(true);
      try {
        const filter = buildMemberFilter({
          authorIds,
          startDate: fromDate,
          endDate: toDate,
        });
        const response = await getEducationalPosts(
          filter,
          POSTS_PER_PAGE,
          pageIndex
        );
        setPosts(response?.data?.items || []);
        setTotal(response?.data?.total || 0);
        setActivePage(pageIndex + 1);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const onPublisherStoreChange = () => {
      setPublishers(publisherStore.getPublishers() || []);
    };

    publisherStore.addChangeListener(onPublisherStoreChange);
    if (publisherStore.getPublishers().length === 0) {
      getPublishers();
    }

    if (location.state?.details) {
      setShowModal(true);
    }

    let initialAuthors = [];
    if (location.state?.analyst) {
      initialAuthors = [location.state.analyst];
      setPublishersSelected(initialAuthors);
    }

    fetchPosts(0, initialAuthors, null, null);

    return () => {
      publisherStore.removeChangeListener(onPublisherStoreChange);
    };
    // Initial load only — matches legacy MemberBlogOverview mount behavior.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const togglePublisher = (publisherId) => {
    setPublishersSelected((current) => {
      const next = current.includes(publisherId)
        ? current.filter((id) => id !== publisherId)
        : [...current, publisherId];
      fetchPosts(0, next, startDate, endDate);
      return next;
    });
  };

  const clearAllFilters = () => {
    setPublishersSelected([]);
    setStartDate(null);
    setEndDate(null);
    setIsClearDates(true);
    fetchPosts(0, [], null, null);
    setTimeout(() => setIsClearDates(false), 0);
  };

  const handleDatesSelected = (range) => {
    const [from, to] = range == null ? [null, null] : range;
    const isCleared = from == null && to == null;
    const isRangeComplete = from != null && to != null;

    if (!isCleared && !isRangeComplete) {
      return;
    }

    const nextStart = from != null ? dayjs(from).format("YYYY-MM-DD") : null;
    const nextEnd = to != null ? dayjs(to).format("YYYY-MM-DD") : null;

    setStartDate(nextStart);
    setEndDate(nextEnd);
    fetchPosts(0, publishersSelected, nextStart, nextEnd);
  };

  const handlePageChange = (pageNumber) => {
    fetchPosts(pageNumber - 1, publishersSelected, startDate, endDate);
    scrollPageToTop();
  };

  // Page 1 only: Lovable lead layout for the newest post (not a "Featured" flag).
  const leadPost = activePage === 1 && posts.length > 0 ? posts[0] : null;
  const gridPosts =
    activePage === 1 && posts.length > 1 ? posts.slice(1) : posts;

  return (
    <MembersPage>
      <HomepageGlobalStyles />
      <Header onSubscribeClick={goToPricing} hideSpacer />

      <MembersHero>
        <MembersHeroBgGradient aria-hidden="true" />
        <MembersHeroBgRadial aria-hidden="true" />
        <MembersHeroInner>
          <MembersHeroTitle>
            The Latest from Your <span>Analyst Team</span>
          </MembersHeroTitle>
        </MembersHeroInner>
      </MembersHero>

      <MembersFiltersSection>
        <MembersContentInner>
          <MembersFilterCard>
            <MembersAnalystFilters>
              <MembersAnalystButton
                type="button"
                $isAll
                $selected={publishersSelected.length === 0}
                aria-pressed={publishersSelected.length === 0}
                title="All analysts"
                onClick={() => {
                  setPublishersSelected([]);
                  fetchPosts(0, [], startDate, endDate);
                }}
              >
                <MembersAllLabel>All</MembersAllLabel>
              </MembersAnalystButton>
              {visiblePublishers.map((publisher) => {
                const selected = publishersSelected.includes(publisher.id);
                return (
                  <MembersAnalystButton
                    key={publisher.id}
                    type="button"
                    $selected={selected}
                    aria-pressed={selected}
                    aria-label={`Filter by ${publisher.name}`}
                    title={publisher.name}
                    onClick={() => togglePublisher(publisher.id)}
                  >
                    <img
                      src={getPublisherAvatarUrl(publisher)}
                      alt=""
                      loading="lazy"
                    />
                  </MembersAnalystButton>
                );
              })}
            </MembersAnalystFilters>

            <MembersFilterActions>
              <MembersDateFilter
                onRangeChange={handleDatesSelected}
                clear={isClearDates}
              />
              {hasActiveFilters && (
                <MembersClearButton type="button" onClick={clearAllFilters}>
                  <span className="material-icons" aria-hidden="true">
                    close
                  </span>
                  Clear filters
                </MembersClearButton>
              )}
            </MembersFilterActions>
          </MembersFilterCard>
        </MembersContentInner>
      </MembersFiltersSection>

      <MembersFeedSection>
        <MembersContentInner>
          {!loggedIn && (
            <MembersGuestBanner>
              <MembersGuestBannerText>
                <p>Browse previews</p>
                <p>
                  Sign in to unlock full posts, or view plans to subscribe.
                </p>
              </MembersGuestBannerText>
              <MembersGuestBannerActions>
                <MembersOutlineButton as={Link} to="/login">
                  Sign in
                </MembersOutlineButton>
                <MembersHeroCtaButton type="button" onClick={goToPricing}>
                  View plans
                </MembersHeroCtaButton>
              </MembersGuestBannerActions>
            </MembersGuestBanner>
          )}

          {loading ? (
            <MemberFeedSkeleton showLead={activePage === 1} />
          ) : posts.length === 0 ? (
            <MembersEmptyState>
              <p>
                {hasActiveFilters
                  ? "Nothing matches your filters — try a wider date range or clear filters."
                  : "There are no member posts to show right now. Check back soon for new content."}
              </p>
              {hasActiveFilters && (
                <MembersClearButton type="button" onClick={clearAllFilters}>
                  <span className="material-icons" aria-hidden="true">
                    close
                  </span>
                  Clear filters
                </MembersClearButton>
              )}
            </MembersEmptyState>
          ) : (
            <>
              {leadPost && <LeadMemberPostCard post={leadPost} />}
              {gridPosts.length > 0 && (
                <MembersGrid>
                  {gridPosts.map((post) => (
                    <MemberPostCard key={post.id} post={post} />
                  ))}
                </MembersGrid>
              )}
              {totalPages > 1 && (
                <MembersPaginationWrap>
                  <MembersPagination
                    activePage={activePage}
                    totalPages={totalPages}
                    onChange={handlePageChange}
                  />
                </MembersPaginationWrap>
              )}
            </>
          )}
        </MembersContentInner>
      </MembersFeedSection>

      {!loggedIn && (
        <MembersCta>
          <MembersCtaBg aria-hidden="true" />
          <MembersCtaInner>
            <MembersCtaTitle>
              Get full access to <span>every post</span>
            </MembersCtaTitle>
            <MembersCtaLead>
              Unlock the complete archive plus daily new content from all seven
              analysts.
            </MembersCtaLead>
            <MembersCtaButton type="button" onClick={goToPricing}>
              Start Your Subscription
            </MembersCtaButton>
          </MembersCtaInner>
        </MembersCta>
      )}

      <Footer onSubscribeClick={goToPricing} hideSubscribeCta={loggedIn} />

      <WelcomeModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        userDetails={userDetails}
        renewal={renewal}
        onTermsClick={() => {
          setShowModal(false);
          navigate("/terms&conditions");
        }}
      />
    </MembersPage>
  );
};

export default Members;
